import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import styles from "../CreatorApplication.module.css";
import GoogleIcon from "../icons/GoogleIcon";
import { registerMemberV2API } from "../../../../api/userApi";
import { showToast } from "../../../../utils/toast";
import { setTokens } from "../../../../utils/tokens";
import { setUser } from "../../../../redux/modules/userSlice";
import { identifyMixpanelUser, setMixpanelPeopleProfile } from "../../../../config/mixpanel";
import { USER_TYPE_IDS } from "../../../Header/ProfileViewer/profile-viewer";

interface Step1Props {
  onNext: (data?: any) => void;
}

const schema = yup.object({
  email: yup
    .string()
    .email("Please enter a valid email")
    .required("Email is required"),
  password: yup
    .string()
    .required("Password is required")
    .min(8, "Must contain 8 or more characters")
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      "Must contain at least one special character"
    ),
  age: yup
    .boolean()
    .oneOf([true], "You must confirm you are at least 18 years old")
    .required(),
  terms: yup
    .boolean()
    .oneOf([true], "You must agree to the Terms and Creator Agreement")
    .required(),
}).required();

type FormData = yup.InferType<typeof schema>;

export default function Step1BasicInfo({ onNext }: Step1Props) {
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("email", data.email.trim());
      formData.append("password", data.password);
      formData.append("usertype", "creator");

      const res: any = await registerMemberV2API(formData);
      const resData = res?.data || res;

      if (resData?.success || res?.status === 200 || res?.status === 201) {
        const tokens = resData?.tokens;
        const user = resData?.user;

        if (tokens) {
          setTokens(tokens);
          if (tokens.access) {
            localStorage.setItem("access_token", tokens.access);
            localStorage.setItem("token", tokens.access);
          }
          if (tokens.refresh) {
            localStorage.setItem("refresh_token", tokens.refresh);
          }
        }
        if (user) {
          const formattedUser = {
            ...user,
            userType: user?.userType || {
              _id: USER_TYPE_IDS.CREATOR,
              title: "creator",
              description: "Creator",
            },
          };
          localStorage.setItem("user", JSON.stringify(formattedUser));
          localStorage.setItem("hasRegisteredOrLoggedIn", "true");
          dispatch(setUser(formattedUser));
          identifyMixpanelUser(formattedUser?.id);
          setMixpanelPeopleProfile({
            email: formattedUser?.email || data.email.trim(),
            name: formattedUser?.username || null,
          });
        }
        showToast(resData?.message || "Registration successful!");
        onNext({ email: data.email.trim(), username: user?.username });
      } else {
        showToast(resData?.message || "Registration failed. Please try again.", "error");
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Registration failed. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.formCard}>
      <h1 className={styles.formTitle}>Apply as a Creator</h1>
      <p className={styles.formSubtitle}>
        Create your creator account. No account-type selection required.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Email */}
        <div className={styles.inputGroup}>
          <label className={styles.label}>
            Email <span className={styles.asterisk}>*</span>
          </label>
          <div className={styles.inputWrapper}>
            <input
              type="email"
              placeholder="you@gmail.com"
              className={styles.input}
              {...register("email")}
            />
          </div>
          {errors.email && <p className={styles.errorText}>{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div className={styles.inputGroup} style={{ marginBottom: "8px" }}>
          <label className={styles.label}>
            Password <span className={styles.asterisk}>*</span>
          </label>
          <div className={styles.inputWrapper}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter password"
              className={styles.input}
              {...register("password")}
            />
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && <p className={styles.errorText}>{errors.password.message}</p>}
        </div>
        <p className={styles.hintText}>
          Must contain 8 or more characters and special symbols
        </p>

        {/* Checkboxes */}
        <div className={styles.checkboxGroup}>
          <input type="checkbox" className={styles.checkbox} id="age" {...register("age")} />
          <label htmlFor="age" className={styles.checkboxLabel}>
            By creating an account, you confirm that you are at least eighteen (18) years of age.
          </label>
        </div>
        {errors.age && (
          <p className={styles.errorText} style={{ marginTop: "-20px", marginBottom: "16px" }}>
            {errors.age.message}
          </p>
        )}

        <div className={styles.checkboxGroup}>
          <input type="checkbox" className={styles.checkbox} id="terms" {...register("terms")} />
          <label htmlFor="terms" className={styles.checkboxLabel}>
            I agree to the <Link to="/terms" className={styles.link}>Terms of Service</Link> and <Link to="/creator-agreement" className={styles.link}>Creator Agreement</Link>
          </label>
        </div>
        {errors.terms && (
          <p className={styles.errorText} style={{ marginTop: "-20px", marginBottom: "16px" }}>
            {errors.terms.message}
          </p>
        )}

        {/* Actions */}
        <button type="submit" className={styles.continueBtn} disabled={isLoading}>
          {isLoading ? "Signing up..." : "Continue"}
        </button>

        <div className={styles.divider}>or</div>

        <button type="button" className={styles.googleBtn}>
          <GoogleIcon />
          Sign Up With Google
        </button>
      </form>

      <p className={styles.footerText}>
        Already have an account? <Link to="/login" className={styles.signInLink}>Sign in</Link>
      </p>
    </div>
  );
}
